/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 *
 * Account Deletion Requests
 *
 * Lists rider/driver accounts a user has requested to delete. Deletion is a
 * 30-day soft-delete: the account is deactivated immediately when requested, and
 * an admin permanently completes the deletion after the 30-day grace period.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  Tooltip, Avatar,
} from '@mui/material';
import { DeleteForever, Undo, Refresh } from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { extractGraphQLError } from '../utils/graphqlError';

const client = generateClient();

const LIST_DELETION_REQUESTS = `
  query ListAccountDeletionRequests {
    listAccountDeletionRequests {
      items { id userType phone name email deletionRequestedAt deletionScheduledAt eligibleForDeletion }
    }
  }
`;

const COMPLETE_DELETION = `
  mutation CompleteAccountDeletion($userId: ID!, $userType: UserType!, $force: Boolean) {
    completeAccountDeletion(userId: $userId, userType: $userType, force: $force) { success message }
  }
`;

const CANCEL_DELETION = `
  mutation CancelAccountDeletion($userId: ID!, $userType: UserType!) {
    cancelAccountDeletion(userId: $userId, userType: $userType) { success message }
  }
`;

interface DeletionRequest {
  id: string;
  userType: 'RIDER' | 'DRIVER';
  phone: string;
  name?: string;
  email?: string;
  deletionRequestedAt?: string;
  deletionScheduledAt?: string;
  eligibleForDeletion: boolean;
}

interface ConfirmState {
  open: boolean;
  action: 'complete' | 'cancel';
  req: DeletionRequest | null;
}

function daysLeft(scheduled?: string): number | null {
  if (!scheduled) return null;
  const ms = new Date(scheduled).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function AccountDeletionRequests() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SuperAdmin');
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ConfirmState>({ open: false, action: 'complete', req: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result: any = await client.graphql({ query: LIST_DELETION_REQUESTS });
      setRequests(result.data?.listAccountDeletionRequests?.items ?? []);
    } catch (e: any) {
      setError(extractGraphQLError(e, 'Failed to load deletion requests'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = (action: 'complete' | 'cancel', req: DeletionRequest) =>
    setDialog({ open: true, action, req });
  const closeDialog = () => setDialog(d => ({ ...d, open: false }));

  const handleConfirm = async () => {
    const { action, req } = dialog;
    if (!req) return;
    setActionLoading(req.id);
    closeDialog();
    try {
      if (action === 'complete') {
        await client.graphql({
          query: COMPLETE_DELETION,
          // SuperAdmin may force before the grace period elapses; others cannot.
          variables: { userId: req.id, userType: req.userType, force: isSuperAdmin && !req.eligibleForDeletion },
        });
      } else {
        await client.graphql({ query: CANCEL_DELETION, variables: { userId: req.id, userType: req.userType } });
      }
      setRequests(rs => rs.filter(r => !(r.id === req.id && r.userType === req.userType)));
    } catch (e: any) {
      setError(extractGraphQLError(e, 'Failed to update deletion request'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Account Deletion Requests</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Users who requested account deletion. Deletion is a 30-day soft-delete: the account is
        deactivated and signed out immediately on request, then permanently deleted here after the
        grace period. Permanent deletion removes the profile and the login; some records may be
        retained where required by law. Cancelling restores the account.
      </Typography>

      {error && (
        <Alert severity={error.includes('permission') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="subtitle1">Pending ({requests.length})</Typography>
          <Tooltip title="Refresh"><IconButton onClick={load} size="small"><Refresh /></IconButton></Tooltip>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Scheduled deletion</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>No pending deletion requests</TableCell></TableRow>
                )}
                {requests.map(r => {
                  const dl = daysLeft(r.deletionScheduledAt);
                  return (
                    <TableRow key={`${r.userType}-${r.id}`} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{(r.name || r.phone || '?')[0].toUpperCase()}</Avatar>
                          <Box>
                            <Typography variant="body2">{r.name || '—'}</Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>{r.id.slice(0, 8)}…</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={r.userType} size="small" color={r.userType === 'DRIVER' ? 'primary' : 'default'} /></TableCell>
                      <TableCell>{r.phone}</TableCell>
                      <TableCell>{r.deletionRequestedAt ? new Date(r.deletionRequestedAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{r.deletionScheduledAt ? new Date(r.deletionScheduledAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>
                        {r.eligibleForDeletion
                          ? <Chip label="Eligible" size="small" color="error" />
                          : <Chip label={dl != null ? `${dl}d left` : 'Pending'} size="small" color="warning" />}
                      </TableCell>
                      <TableCell align="center">
                        {actionLoading === r.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            <Tooltip title="Cancel deletion (restore account)">
                              <IconButton size="small" color="success" onClick={() => openDialog('cancel', r)}>
                                <Undo fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={
                              r.eligibleForDeletion
                                ? 'Permanently delete'
                                : isSuperAdmin ? 'Force permanent delete (before grace period)' : 'Grace period not elapsed'
                            }>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!r.eligibleForDeletion && !isSuperAdmin}
                                  onClick={() => openDialog('complete', r)}
                                >
                                  <DeleteForever fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog.action === 'complete' ? '🗑️ Permanently Delete Account' : '↩️ Cancel Deletion'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialog.action === 'complete'
              ? `Permanently delete ${dialog.req?.name || dialog.req?.phone}? This removes the profile and the login and cannot be undone. Some records may be retained where required by law.`
              : `Cancel the deletion for ${dialog.req?.name || dialog.req?.phone}? Their account will be restored and re-enabled.`}
          </DialogContentText>
          {dialog.action === 'complete' && dialog.req && !dialog.req.eligibleForDeletion && isSuperAdmin && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              The 30-day grace period has not elapsed. As SuperAdmin you can force deletion now.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog.action === 'complete' ? 'error' : 'success'}
            onClick={handleConfirm}
          >
            {dialog.action === 'complete' ? 'Delete Permanently' : 'Restore Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
